// #351, brief §5.4 and §15 rows 29 and 33: the one generator of a program
// model. The athlete's choices (level, focus, minutes, sessions a week,
// blocks, the exercise of each row) pick the rows and their rules; each rule writes its
// cells (rowsForRule), and every block after the first is flow 9
// (addNextBlock). Keep free of React and any UI import.
import { rirToRpe, rpeToRir, type EffortScale } from "./effortScale.js";
import type { Locale } from "./locale.js";
import { blankSlot, type Draft, type SlotDraft } from "./programEdit.js";
import {
  modelDayName,
  modelRowEntry,
  type ModelLevel,
  type ModelRow,
  type ProgramModel,
  type SessionMinutes,
} from "./programModel.js";
import { effortOn, harderSign, type ProgressionRule, type Sequence } from "./programProgression.js";
import { rowsForRule } from "./programProgressionApply.js";
import { addNextBlock } from "./programProgressionRounds.js";
import type { ProgramFocus, RoundType } from "./schemas/programV3.js";
import { uuidv4 } from "./uuid.js";

export type ModelChoices = {
  name: string;
  level: ModelLevel;
  /** #356: absent, every row as byLevel writes it. */
  focus?: ProgramFocus;
  minutes: SessionMinutes;
  /** One of the model's values: the caller offers only those. */
  sessionsPerWeek: number;
  /** How many times the round pattern runs; each block after the first
   *  starts again a step higher. */
  blocks: number;
  /** A row's exercise, by row id. Only the row's own alternatives count. */
  exercisePicks?: Record<string, string>;
  effortScale: EffortScale;
  /** The language the program is written in (Draft.editLocale). */
  editLocale?: Locale | null;
  /** The catalog name of a slug, stored with the slot. */
  exerciseName?: (slug: string) => string | undefined;
  /** #356: the model's id; with it the program keeps its origin. */
  modelId?: string;
};

type Num = (x: number) => number;

/** `seq` with its values through `v` and its steps through `d`. */
const mapSeq = (seq: Sequence, v: Num, d: Num): Sequence => {
  switch (seq.kind) {
    case "constant":
      return { kind: "constant", value: v(seq.value) };
    case "linear":
      return { kind: "linear", start: v(seq.start), step: d(seq.step), ...(seq.cap !== undefined ? { cap: v(seq.cap) } : {}) };
    case "ramp":
      return { ...seq, from: v(seq.from), to: v(seq.to) };
    case "cycle":
      return { kind: "cycle", values: seq.values.map(v) };
    case "blocks":
      return { kind: "blocks", blocks: seq.blocks.map((b) => ({ ...b, seq: mapSeq(b.seq, v, d) })) };
  }
};

/** `rule` on `axis`: its values through `v`, its steps, offsets and deltas
 *  through `d`. A deload factor is a ratio and stays. */
export const mapRuleValues = (rule: ProgressionRule, axis: ProgressionRule["axis"], v: Num, d: Num): ProgressionRule => {
  const { perSet, threshold, test, deload } = rule;
  return {
    ...rule,
    axis,
    value: rule.value && mapSeq(rule.value, v, d),
    ...(perSet ? { perSet: { ...perSet, ...(perSet.value ? { value: perSet.value.map(d) } : {}) } } : {}),
    ...(threshold ? { threshold: { ...threshold, loadStart: v(threshold.loadStart), loadStep: d(threshold.loadStep) } } : {}),
    ...(test ? { test: { ...test, ...(test.delta === undefined ? {} : { delta: d(test.delta) }) } } : {}),
    ...(deload ? { deload: { ...deload, ...(deload.delta === undefined ? {} : { delta: d(deload.delta) }) } } : {}),
  };
};

/** A step on `axis` written on the other effort scale: RIR turns its sign. */
const effortStep = (axis: ProgressionRule["axis"]): Num => (x) => (x === 0 ? 0 : x * harderSign(axis));

/** #356: a history row's deload effort (DeloadEffect.effort) on `scale`. */
const deloadEffortOn = (rule: ProgressionRule, scale: EffortScale): ProgressionRule => {
  const e = rule.deload?.effort;
  if (!e || e.axis === scale) return rule;
  const value = scale === "rir" ? rpeToRir(e.value) : rirToRpe(e.value);
  return { ...rule, deload: { ...rule.deload, effort: { axis: scale, value } } };
};

/** A model rule on the athlete's effort scale. Models store effort in RPE
 *  (§15 row 29); any other axis is left as it is. */
export const ruleOnScale = (rule: ProgressionRule, scale: EffortScale): ProgressionRule => {
  const r = deloadEffortOn(rule, scale);
  if (r.axis !== "rpe" || scale === "rpe") return r;
  return mapRuleValues(r, scale, (x) => effortOn(x, scale), effortStep(scale));
};

/** ruleOnScale's inverse: a rule in RIR written in RPE, as a model stores it
 *  (#354). Any other axis is left as it is. */
export const ruleToRpe = (rule: ProgressionRule): ProgressionRule => {
  const r = deloadEffortOn(rule, "rpe");
  if (r.axis !== "rir") return r;
  return mapRuleValues(r, "rpe", rirToRpe, effortStep("rir"));
};

/** One set fewer, never below one: the per-set values lose their last entry,
 *  and a threshold's set ceiling comes down with it. */
export const oneSetFewer = (rule: ProgressionRule): ProgressionRule => {
  const sets = Math.max(1, rule.sets - 1);
  if (sets === rule.sets) return rule;
  const { perSet, threshold } = rule;
  return {
    ...rule,
    sets,
    ...(perSet
      ? {
          perSet: {
            ...(perSet.reps ? { reps: perSet.reps.slice(0, sets) } : {}),
            ...(perSet.value ? { value: perSet.value.slice(0, sets) } : {}),
          },
        }
      : {}),
    ...(threshold?.setsMax !== undefined
      ? { threshold: { ...threshold, setsMax: Math.max(sets, threshold.setsMax - 1) } }
      : {}),
  };
};

type ModelAt = NonNullable<ReturnType<typeof modelRowEntry>>;
type ModelEntry = ModelAt["entry"];

/** The row's entry at `level` and `focus`; null when the row is not there
 *  at these choices (no entry for the level, or an optional row under 75
 *  minutes). */
export const modelRowAt = (
  row: ModelRow,
  level: ModelLevel,
  minutes: SessionMinutes,
  focus?: ProgramFocus,
): ModelEntry | null => modelSlotAt(row, level, minutes, focus)?.entry ?? null;

const modelSlotAt = (row: ModelRow, level: ModelLevel, minutes: SessionMinutes, focus?: ProgramFocus): ModelAt | null => {
  const at = modelRowEntry(row, level, focus);
  return !at || (at.weight === "optional" && minutes < 75) ? null : at;
};

/** The row's exercise: the pick when it is one of the row's own, else the
 *  default (the first). */
export const modelRowExercise = (entry: ModelEntry, pick: string | undefined): string =>
  pick !== undefined && entry.exercises.includes(pick) ? pick : entry.exercises[0]!;

/** The slot a row gives at the athlete's choices, or null (modelRowAt). */
export const modelRowSlot = (row: ModelRow, choices: ModelChoices, types: readonly RoundType[]): SlotDraft | null => {
  const at = modelSlotAt(row, choices.level, choices.minutes, choices.focus);
  if (!at) return null;
  const { entry, weight } = at;
  const exerciseId = modelRowExercise(entry, choices.exercisePicks?.[row.id]);
  let rule = ruleOnScale(entry.rule, choices.effortScale);
  if (weight === "accessory" && choices.minutes === 45) rule = oneSetFewer(rule);
  const rest = entry.restSec === undefined ? undefined : String(entry.restSec);
  const setRows = rowsForRule(rule, types, types.length);
  return {
    ...blankSlot(types.length),
    exerciseId,
    exerciseName: choices.exerciseName?.(exerciseId),
    setRows: rest === undefined ? setRows : setRows.map((r) => ({ ...r, cells: r.cells.map((c) => (c.absent ? c : { ...c, restSec: rest })) })),
  };
};

/** A new program from `model` at the athlete's choices. Day names in the
 *  edit language, else English. */
export const buildModelDraft = (model: Omit<ProgramModel, "id">, choices: ModelChoices): Draft => {
  const types = model.roundPattern;
  const lang = choices.editLocale ?? "en";
  let draft: Draft = {
    name: choices.name,
    description: "",
    ...(choices.editLocale !== undefined ? { editLocale: choices.editLocale } : {}),
    ...(choices.editLocale ? { declaredAuthorLocale: choices.editLocale } : {}),
    status: "draft",
    cycleWeeks: types.length,
    sessionsPerWeek: choices.sessionsPerWeek,
    roundTypes: [...types],
    ...(choices.focus ? { focus: choices.focus } : {}),
    ...(choices.modelId
      ? {
          origin: {
            modelId: choices.modelId,
            level: choices.level,
            minutes: choices.minutes,
            picks: { ...choices.exercisePicks },
          },
        }
      : {}),
    sessions: model.days.map((day) => ({
      id: uuidv4(),
      name: modelDayName(day, choices.focus, lang),
      notes: "",
      slots: day.rows.flatMap((row) => modelRowSlot(row, choices, types) ?? []),
    })),
    assignedTo: [],
    assigneeEmails: [],
  };
  for (let b = 1; b < choices.blocks; b += 1) {
    const next = addNextBlock(draft, { rounds: types.length, types });
    if (!next.ok) break;
    draft = next.draft;
  }
  return draft;
};
